import { useState, useRef } from 'react';

/**
 * VoiceInput — wraps browser SpeechRecognition API.
 * Calls onTranscript(text) when the user finishes speaking.
 * Calls onListeningChange(bool) so the parent can show/hide the banner.
 */
export default function VoiceInput({ onTranscript, onListeningChange }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert('Speech Recognition is not supported in this browser. Try Chrome.');
      return;
    }

    // Stop any existing session first
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setListening(true);
      onListeningChange?.(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      onTranscript(transcript);
    };

    recognition.onend = () => {
      setListening(false);
      onListeningChange?.(false);
    };

    recognition.onerror = (err) => {
      if (err.error !== 'no-speech' && err.error !== 'aborted') {
        console.error('Speech recognition error:', err);
      }
      setListening(false);
      onListeningChange?.(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopListening = () => {
    recognitionRef.current?.abort();
    setListening(false);
    onListeningChange?.(false);
  };

  return (
    <button
      id="chatbot-mic-btn"
      className={`chatbot-mic-btn ${listening ? 'listening' : ''}`}
      onClick={listening ? stopListening : startListening}
      title={listening ? 'Stop listening' : 'Speak your message'}
      type="button"
      aria-label={listening ? 'Stop voice input' : 'Start voice input'}
    >
      {listening ? (
        /* Stop / mic-slash icon */
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="1" y1="1" x2="23" y2="23"/>
          <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
          <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .83-.13 1.63-.38 2.38"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
      ) : (
        /* Mic icon */
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
      )}
    </button>
  );
}
